import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ArchiveDepartmentModal from "../../components/DeleteDepartmentsModal";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { graphqlEndpoint } from "../../config";
import { READ_DEPARTMENTS_BY_TENANT } from "../../graph/departments/queries";
import { DELETE_DEPARTMENT } from "../../graph/departments/mutations";
import { useTenant } from "../../context/TenantContext";

const client = new GraphQLClient(graphqlEndpoint);

const ManageDepartments = () => {
  const navigate = useNavigate();
  const { setSnackbar } = useSnackbar();
  const { tenantId } = useTenant();

  const [rows, setRows] = useState([]);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const fetchData = useCallback(async () => {
    if (!tenantId) {
      setRows([]);
      return;
    }

    try {
      const data = await client.request(READ_DEPARTMENTS_BY_TENANT, {
        tenantId,
      });

      setRows(
        (data.readDepartmentsByTenant || []).map((department) => ({
          ...department,
          id: department.id,
        }))
      );
    } catch (err) {
      console.error("Failed to fetch departments:", err);
      setRows([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load departments",
      });
    }
  }, [tenantId, setSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenArchiveModal = (department) => {
    setSelectedDepartment(department);
    setArchiveModalOpen(true);
  };

  const handleCloseArchiveModal = () => {
    setArchiveModalOpen(false);
    setSelectedDepartment(null);
  };

  const handleConfirmArchive = async () => {
    if (!selectedDepartment?.id) return;

    try {
      await client.request(DELETE_DEPARTMENT, { id: selectedDepartment.id });

      await fetchData();
      handleCloseArchiveModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Department deleted successfully",
      });
    } catch (err) {
      console.error("Failed to delete department:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete department",
      });
    }
  };

  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "description", headerName: "Description", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <TableActions
          onEdit={() => navigate(`/departments/edit/${row.id}`)}
          onDelete={() => handleOpenArchiveModal(row)}
          hideCreate
        />
      ),
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header title="DEPARTMENTS" subtitle="Departments group related business outcomes" />
        <ActionButton
          onClick={() => navigate("/createDepartment")}
          tone="gold"
          disabled={!tenantId}
        >
          Create Department
        </ActionButton>
      </Box>

      {!tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view departments.
          </Typography>
        </Box>
      ) : (
        <ThemedDataGrid rows={rows} columns={columns} />
      )}

      <ArchiveDepartmentModal
        open={archiveModalOpen}
        onClose={handleCloseArchiveModal}
        onConfirm={handleConfirmArchive}
        department={selectedDepartment}
      />
    </Box>
  );
};

export default ManageDepartments;